// How the map box moves between views, and what the views do on the way.
//
// This file used to have three jobs. Two of them were consequences of the header
// and left with it:
//
//   — publishing --header-h and --diag-left/right, because the city diagrams had
//     to line up with the header's progress track. The ruler is a screen-edge
//     element and the briefing is a guttered document, so the two are no longer
//     related and there is nothing to measure.
//   — relocating chrome below the mobile breakpoint, because #topbar's
//     backdrop-filter made a fixed bottom bar inside it impossible. There is no
//     header and no bottom bar; the phone is a stylesheet question.
//
// What is left is the one that was never about the chrome: FLIP-animating the
// map container between the full-bleed map and the city band, because #map is
// position:fixed with no CSS size transition and a plain resize() snaps the
// canvas — and its centred point — in one frame.

import { $ } from '../dom.ts';
import { map, hasMap } from '../map/instance.ts';
import { state } from '../state.ts';
import type { ViewName } from '../state.ts';
import { track, trackPageviewDeferred } from '../analytics.ts';
import { MOBILE, TABLET, REDUCED_MOTION } from './breakpoints.ts';
import { setSelectedHighlight } from '../map/layers.ts';
import { renderCity, reframeCity, clearCityFrame } from '../city/panel.ts';
import { invalidateRulerWidth } from './ruler.ts';
import { refreshPeaks } from '../myday.ts';
import { renderCornerWords } from './chrome.ts';
import { onLeaveCompare, renderCompare } from '../compare/view.ts';
import { renderSentence } from './sentence.ts';
import { positionRing } from './tooltip.ts';

// What layout needs the app to do. Small on purpose: these are the four things
// that belong to other concerns entirely.
export interface LayoutDeps {
  closeLayerMenu: () => void;
  persist: () => void;
  markUserCamera: () => void;
  pause: () => void;
}

let deps: LayoutDeps;

export function initLayout(d: LayoutDeps): void {
  deps = d;
}

export function onBreakpointChange() {
  // Nothing MOVES across the breakpoint any more, and nothing is measured. The
  // corner chrome and the bottom-edge ruler serve both widths, so all that is
  // left is closing what is open and rebuilding what is genuinely per-tier.
  deps.closeLayerMenu();
  // The city body's structure differs per mode (records rail, temp-chart width) —
  // rebuild it from the series already in hand.
  if (state.view === 'city' && state.series) renderCity();
  if (state.view === 'compare') renderCompare();
  // Crossing into mobile leaves any desktop band-scroll transform on the rail,
  // which the mobile sheet must not keep; clear it and let onCityScroll re-apply
  // it if we cross back.
  if (MOBILE.matches) $('peaks').style.transform = '';
  if (hasMap()) map.resize();
}

// Crossing the tablet (≤1180) boundary changes only the temp chart's viewBox.
// The ruler's month labels are one length at every width — three letters fit the
// narrowest cell it has, so the three-label-set dance the old track needed is
// gone with it.
export function onTabletChange() {
  if (state.view === 'city' && state.series) renderCity();
  if (state.view === 'compare') renderCompare();
}

const MAP_BOX_MS = 420;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

// Give the box back to the stylesheet. Both ends of the animation need this: the
// start, to read the true CSS target rather than a box left pinned by an
// interrupted run, and the finish, to hand control back so a later resize is
// CSS's business again.
function releaseMapBoxToCss(el: HTMLElement) {
  el.style.top = el.style.left = el.style.width = el.style.height = el.style.right = el.style.bottom = '';
}

// Hold the box at an exact rect, overriding the stylesheet for the duration.
function pinMapBox(el: HTMLElement, top: number, left: number, width: number, height: number) {
  el.style.right = 'auto';
  el.style.bottom = 'auto';
  el.style.top = `${top}px`;
  el.style.left = `${left}px`;
  el.style.width = `${width}px`;
  el.style.height = `${height}px`;
}

let mapBoxRAF = 0;
let mapBoxSafety: ReturnType<typeof setTimeout> | undefined;
// Smoothly grow/shrink the map container between the full-bleed map and the
// city band. We FLIP: measure the box before and after the view swap, then
// interpolate #map's box, re-measuring the map each frame so the canvas tracks
// the animating band. A concurrent flyTo re-projects every frame, so the city
// still settles centred in the band.
function animateMapBox(from: DOMRect) {
  const el = map.getContainer();
  if (mapBoxRAF) { cancelAnimationFrame(mapBoxRAF); mapBoxRAF = 0; }

  releaseMapBoxToCss(el);
  const to = el.getBoundingClientRect();
  const delta = Math.abs(from.top - to.top) + Math.abs(from.height - to.height)
    + Math.abs(from.left - to.left) + Math.abs(from.width - to.width);
  if (delta < 1) { map.resize(); return; } // same box (e.g. landing↔map) — nothing to animate

  // No animation when nobody can watch it, and none when they have asked not to.
  // This is not only politeness: requestAnimationFrame does not fire in a hidden
  // tab, so a run started there would never reach its final frame — and the
  // final frame is what hands the box back to the stylesheet. Switching tabs
  // mid-transition used to leave the map pinned at whatever size the
  // interpolation had reached, with the briefing laid out around it.
  if (document.hidden || REDUCED_MOTION.matches) { map.resize(); return; }

  const start = performance.now();
  const frame = (now: number) => {
    const k = easeOutCubic(Math.min(1, (now - start) / MAP_BOX_MS));
    pinMapBox(
      el,
      from.top + (to.top - from.top) * k,
      from.left + (to.left - from.left) * k,
      from.width + (to.width - from.width) * k,
      from.height + (to.height - from.height) * k,
    );
    map.resize();
    if (k < 1) {
      mapBoxRAF = requestAnimationFrame(frame);
      return;
    }
    releaseMapBoxToCss(el);
    map.resize();
    mapBoxRAF = 0;
  };
  mapBoxRAF = requestAnimationFrame(frame);
  // Belt and braces for the same failure: if the loop stalls for any other
  // reason, release the box on a timer so the layout cannot stay pinned.
  clearTimeout(mapBoxSafety);
  mapBoxSafety = setTimeout(() => {
    if (!mapBoxRAF) return;
    cancelAnimationFrame(mapBoxRAF);
    mapBoxRAF = 0;
    releaseMapBoxToCss(el);
    map.resize();
  }, MAP_BOX_MS * 2);
}

export function setView(v: ViewName) {
  // Landing, map and city briefing are what a reader would call three pages, so
  // each arrival is both an event and a page view. Both sit out a route replay.
  if (v !== state.view) {
    track('view_change', { view: v });
    trackPageviewDeferred(); // after the URL this view produces has been written
  }
  // Drop any scroll-away offset first, or onCityScroll's translate would be
  // measured as part of the box the animation starts from.
  map.getContainer().style.transform = '';
  const from = map.getContainer().getBoundingClientRect();
  const was = state.view;
  state.view = v;
  document.body.classList.remove('view-landing', 'view-map', 'view-city', 'view-compare');
  document.body.classList.add(`view-${v}`);
  animateMapBox(from);
  // In a briefing the map is a header on a page that has to scroll, so the wheel
  // belongs to the page; the map keeps drag-to-pan. Everywhere else it zooms.
  if (v === 'city') map.scrollZoom.disable();
  else {
    map.scrollZoom.enable();
    // The band's camera padding belongs to the band; the map is framed by nothing.
    if (was === 'city') clearCityFrame();
  }
  $('cityscroll').scrollTop = 0;
  $('cmpscroll').scrollTop = 0;
  if (was === 'compare' && v !== 'compare') onLeaveCompare();
  // The sentence says different things in a comparison ("for sunbathing"), and
  // the corner words light whichever page is open.
  renderSentence();
  renderCornerWords();
  // Leaving the city view must not strand the corner chrome at the band's last
  // scroll offset — onCityScroll re-applies it the moment there is a band again.
  for (const id of ['corner', 'corner-right', 'top-scrim', 'ruler-block']) {
    $(id).style.transform = '';
  }
  refreshPeaks({ immediate: true }); // the panel belongs to the map surface
  positionRing(); // the open city's pulse belongs to the briefing only
  deps.persist();
}

// City-view scroll: slide the fixed map band up with the page so it scrolls away
// rather than sitting still, and take everything that belongs to the map with
// it. Nothing pins: the briefing is a document to read down, and a bar riding
// its top is the header this redesign removed.
export function onCityScroll() {
  if (state.view !== 'city' && state.view !== 'compare') return;
  const compare = state.view === 'compare';
  const st = $(compare ? 'cmpscroll' : 'cityscroll').scrollTop;
  const band = $(compare ? 'cmp-band' : 'city-band').offsetHeight || 1;
  const y = Math.min(st, band);
  map.getContainer().style.transform = `translateY(${-y}px)`;
  // On desktop the peaks panel sits ON the map band, so it scrolls away with it
  // rather than hanging over the briefing below. On mobile it is not on the
  // band at all — it is a sheet pinned above the ruler — and shifting it by the
  // band's offset would sail it off the top of the screen.
  const panel = $('peaks');
  if (panel && !panel.hidden) {
    panel.style.transform = MOBILE.matches ? '' : `translateY(${-y}px)`;
  }
  // Everything the map brought with it leaves with it: the sentence and the two
  // corner words at the top, their scrim, and the ruler on the band's lower
  // edge. None of them describe the briefing — they describe the map, and once
  // the map has gone they would be chrome hanging over a document, which is the
  // header this redesign removed wearing a different hat.
  const shift = `translateY(${-y}px)`;
  for (const id of ['corner', 'corner-right', 'top-scrim', 'ruler-block']) {
    $(id).style.transform = shift;
  }
  positionRing(); // the open city's pulse rides the band, and leaves with it
}

export function goLanding() {
  state.selectedCity = null;
  state.series = null;
  setSelectedHighlight(null);
  deps.pause(); // landing is still
  setView('landing');
}

export function goMap() {
  setSelectedHighlight(null);
  // Entering the map by an explicit control (Explore / maximise) names the camera
  // the reader is looking at — the URL becomes @lat,lng,zoom, not a bare path.
  deps.markUserCamera();
  setView('map');
}

// Which tier was last APPLIED — not which the width implies. The difference is
// the point: some embedded/emulated browsers miss the matchMedia change event on
// resize, so the resize handler compares the two and catches up. Nothing MOVES
// across these boundaries any more, but the city body's structure still differs
// by tier, so a missed crossing still leaves the wrong markup on screen.
const mobileState = { now: MOBILE.matches };
const tabletState = { now: TABLET.matches };

// ── wiring ──────────────────────────────────────────────────────────────────
// The view-navigation buttons and every width/scroll listener layout answers to.
export function wireLayout() {
  // Brand → landing; ghost/explore → map; maximise the city map band → full map.
  $('brand').addEventListener('click', goLanding);
  $('explore-map').addEventListener('click', goMap);

  // City-view scroll: the map band scrolls away with the page (translate it up in
  // lockstep with the scroll) until it's fully hidden; past that, the sticky city
  // header pins and gains a solid background + a left inset so the FIXED brand logo
  // (which never moves) sits on solid chrome without covering the city name.
  $('cityscroll').addEventListener('scroll', onCityScroll);
  $('cmpscroll').addEventListener('scroll', onCityScroll);
  let reframeTimer: ReturnType<typeof setTimeout> | undefined;
  window.addEventListener('resize', () => {
    // The band is sized by the window, and the open city is framed in the band.
    clearTimeout(reframeTimer);
    reframeTimer = setTimeout(reframeCity, 120);
    invalidateRulerWidth(); // the sweep measures the ruler once and caches it
    // Catches up the layout when matchMedia missed the crossing (see mobileState).
    // Cheap — no-ops when nothing crossed.
    if (MOBILE.matches !== mobileState.now) { mobileState.now = MOBILE.matches; onBreakpointChange(); }
    // Crossing the tablet band swaps the temp chart's viewBox (wide desktop ↔
    // narrow single-column); guarded so it no-ops mid-drag.
    if (TABLET.matches !== tabletState.now) { tabletState.now = TABLET.matches; onTabletChange(); }
  });
  MOBILE.addEventListener('change', () => { mobileState.now = MOBILE.matches; onBreakpointChange(); });
  // Crossing the tablet band re-renders the city so the temp chart picks its
  // single-column viewBox — the rest of the tier is pure CSS.
  TABLET.addEventListener('change', () => { tabletState.now = TABLET.matches; onTabletChange(); });
}
